import { isNativeCapacitorRuntime } from './revenueCatService';
import { dispatchMobileEvent, compactProperties } from './mobileAnalyticsClient';

// First-party mobile RETENTION / session instrumentation.
//
// Why this is a mobile-specific gap: the app is a Capacitor WKWebView wrapper,
// and the WebView keeps the React page alive across background/foreground. So
// when a user leaves the app and comes back WITHOUT a page reload, the existing
// page-load-based web analytics (Segment page(), GTM) fire nothing — the
// re-engagement is invisible. Native `appStateChange` is the only signal for
// "the user came back", which is the core mobile retention/DAU measure.
//
// Emits first-party events (Segment survives the WebView; Mixpanel no-op on
// mobile) via the shared mobileAnalyticsClient, so it inherits the never-throw
// guarantee. Native-only; a no-op on web. The @capacitor/app plugin is loaded
// via dynamic import so web builds never resolve it eagerly.

export const MOBILE_RETENTION_EVENTS = {
  APP_RESUMED: 'Mobile App Resumed',
  APP_BACKGROUNDED: 'Mobile App Backgrounded'
};

// Guards against double-binding the appStateChange listener on repeated init.
let retentionListenerBound = false;

const trackRetentionEvent = (event, properties = {}) =>
  dispatchMobileEvent(event, {
    platform: 'ios',
    category: 'retention',
    ...compactProperties(properties)
  });

// Exposed for direct dispatch + testing. `isActive` is the appStateChange
// payload: true = returned to foreground (re-engagement), false = backgrounded.
// Note: iOS also fires transient resignActive/becomeActive pairs for Control
// Center / Notification Center / permission dialogs, so downstream analysis
// should dedupe (e.g. count distinct-users-per-day) rather than count raw
// resumes. Strict boolean checks avoid emitting on a malformed payload.
export const handleAppStateChange = ({ isActive } = {}) => {
  if (isActive === true) {
    return trackRetentionEvent(MOBILE_RETENTION_EVENTS.APP_RESUMED);
  }
  if (isActive === false) {
    return trackRetentionEvent(MOBILE_RETENTION_EVENTS.APP_BACKGROUNDED);
  }
  return false;
};

// Entry point (call once at app bootstrap): start emitting foreground/background
// retention events. No-op off the native runtime; fails soft if @capacitor/app
// is unavailable. Never throws.
export const initMobileRetentionTracking = async () => {
  if (!isNativeCapacitorRuntime()) {
    return { started: false, reason: 'not-native' };
  }

  if (retentionListenerBound) {
    return { started: true, reason: 'already-initialized' };
  }

  let App;
  try {
    ({ App } = await import('@capacitor/app'));
  } catch (importError) {
    return { started: false, reason: 'plugin-unavailable' };
  }

  if (!App) {
    return { started: false, reason: 'plugin-unavailable' };
  }

  try {
    App.addListener('appStateChange', (state) => {
      handleAppStateChange(state);
    });
    // Set only after a successful bind so a throwing addListener doesn't
    // permanently latch the guard.
    retentionListenerBound = true;
    return { started: true };
  } catch (error) {
    return { started: false, reason: 'init-exception' };
  }
};

// Test seam: reset the one-time listener guard.
export const resetRetentionListenerBindingForTests = () => {
  retentionListenerBound = false;
};
